import { Link, createFileRoute } from '@tanstack/react-router'
import { useCallback, useState } from 'react'
import { ArrowLeft, ArrowUpDown, Filter, Loader2, Trash2 } from 'lucide-react'
import type {
  BankTransaction,
  BankTransactionFilters,
  BankTransactionQuery,
} from '@/types/bank-transaction'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { PaginationNext, PaginationPrevious } from '@/components/ui/pagination'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'
import TitleHeader from '@/components/TitleHeader'
import { useBankTransactions } from '@/hooks/useBankTransactions'
import { TIME_RANGE_OPTIONS } from '@/types/balance'

export const Route = createFileRoute(
  '/_authenticated/activity/deposit-history/',
)({
  component: BankHistoryManagement,
})

// Loading skeleton component for table rows
const TableSkeleton = () => (
  <>
    {[...Array(10)].map((_, i) => (
      <TableRow key={i}>
        <TableCell>
          <Skeleton className="h-4 w-32" />
        </TableCell>
        <TableCell>
          <Skeleton className="h-4 w-24" />
        </TableCell>
        <TableCell>
          <Skeleton className="h-6 w-20 rounded-full" />
        </TableCell>
        <TableCell>
          <Skeleton className="h-4 w-64" />
        </TableCell>
      </TableRow>
    ))}
  </>
)

function BankHistoryManagement() {
  const [searchFilters, setSearchFilters] = useState<BankTransactionFilters>({
    transactionId: '',
    bank: '',
    transferContent: '',
    timeRange: '',
  })
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize] = useState(20)
  const [appliedFilters, setAppliedFilters] =
    useState<BankTransactionFilters>(searchFilters)

  // Convert filters to API query
  const apiQuery: BankTransactionQuery = {
    page: currentPage,
    take: pageSize,
    sorts: 'createdAt.DESC',
    isSelectAll: false,
    providerTransactionId: appliedFilters.transactionId || undefined,
    provider: appliedFilters.bank || undefined,
    description: appliedFilters.transferContent || undefined,
    // Add time range logic here if needed
  }

  // Fetch data from API
  const {
    data: bankTransactionsResponse,
    isLoading: bankTransactionsLoading,
    error: bankTransactionsError,
  } = useBankTransactions(apiQuery)

  // Be resilient to various response shapes
  const bankTransactionsItems =
    (bankTransactionsResponse as any)?.data?.items ??
    (bankTransactionsResponse as any)?.items ??
    bankTransactionsResponse ??
    []
  const bankTransactions: BankTransaction[] = Array.isArray(
    bankTransactionsItems,
  )
    ? bankTransactionsItems
    : []
  const pagination = (bankTransactionsResponse as any)?.data?.meta ??
    (bankTransactionsResponse as any)?.meta ?? {
      total: 0,
      page: currentPage,
      limit: pageSize,
      totalPages: 1,
      hasNext: false,
      hasPrev: false,
    }

  const handleSearch = useCallback(() => {
    setAppliedFilters(searchFilters)
    setCurrentPage(1)
  }, [searchFilters])

  const clearFilters = useCallback(() => {
    const emptyFilters: BankTransactionFilters = {
      transactionId: '',
      bank: '',
      transferContent: '',
      timeRange: '',
    }
    setSearchFilters(emptyFilters)
    setAppliedFilters(emptyFilters)
    setCurrentPage(1)
  }, [])

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page)
  }, [])

  const formatCurrency = (amount: string | number) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount
    return new Intl.NumberFormat('vi-VN').format(numAmount) + ' đ'
  }

  const formatDateTime = (timestamp: string) => {
    const date = new Date(timestamp)
    const time = date.toLocaleTimeString('vi-VN', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
    const dateStr = date.toLocaleDateString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
    return `${time} - ${dateStr}`
  }

  const getBankDisplayName = (provider: string) => {
    const bankMap: Record<string, string> = {
      sieuthicode: 'BIDV',
      // Add more bank mappings as needed
    }
    return bankMap[provider] || provider
  }

  return (
    <div className="p-6">
      <div className="mb-4">
        <Link to="/activity">
          <Button variant="ghost" className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Quay lại
          </Button>
        </Link>
      </div>
      <TitleHeader
        title="Lịch sử ngân hàng"
        subtitle="Chào mừng bạn đến với Trung tâm thông tin dữ liệu của bạn!"
      />

      <Card className="mt-6 shadow-none">
        <CardHeader>
          <CardTitle className="text-lg font-semibold">
            Lịch sử giao dịch ngân hàng
          </CardTitle>
        </CardHeader>

        <Separator />

        <CardContent className="p-0">
          <div className="filter mb-5 px-4">
            <div className="grid grid-cols-4 gap-4 mb-5">
              <Input
                placeholder="Mã giao dịch"
                value={searchFilters.transactionId}
                onChange={(e) =>
                  setSearchFilters((prev) => ({
                    ...prev,
                    transactionId: e.target.value,
                  }))
                }
              />
              <Input
                placeholder="Ngân hàng"
                value={searchFilters.bank}
                onChange={(e) =>
                  setSearchFilters((prev) => ({
                    ...prev,
                    bank: e.target.value,
                  }))
                }
              />
              <Input
                placeholder="Nội dung chuyển khoản"
                value={searchFilters.transferContent}
                onChange={(e) =>
                  setSearchFilters((prev) => ({
                    ...prev,
                    transferContent: e.target.value,
                  }))
                }
              />
              <Select
                value={searchFilters.timeRange || undefined}
                onValueChange={(value) =>
                  setSearchFilters((prev) => ({ ...prev, timeRange: value }))
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Chọn thời gian" />
                </SelectTrigger>
                <SelectContent>
                  {TIME_RANGE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Button
                variant="outline"
                className="mr-3"
                onClick={handleSearch}
                disabled={bankTransactionsLoading}
              >
                {bankTransactionsLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Filter className="h-4 w-4 mr-2" />
                )}
                Lọc
              </Button>
              <Button variant="outline" className="mr-3" onClick={clearFilters}>
                <Trash2 className="h-4 w-4 mr-2" />
                Xóa bộ lọc
              </Button>
            </div>
          </div>

          <Table className="">
            <TableHeader className="bg-gray-100">
              <TableRow>
                <TableHead>
                  <div className="flex items-center gap-2">
                    Thời gian
                    <ArrowUpDown className="h-4 w-4 text-gray-400" />
                  </div>
                </TableHead>
                <TableHead>
                  <div className="flex items-center gap-2">
                    Ngân hàng
                    <ArrowUpDown className="h-4 w-4 text-gray-400" />
                  </div>
                </TableHead>
                <TableHead>
                  <div className="flex items-center gap-2">
                    Số tiền nạp
                    <ArrowUpDown className="h-4 w-4 text-gray-400" />
                  </div>
                </TableHead>
                <TableHead>Nội dung chuyển khoản</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bankTransactionsLoading ? (
                <TableSkeleton />
              ) : bankTransactionsError ? (
                <TableRow>
                  <TableCell colSpan={4} className="px-4 py-12 text-center">
                    <div className="text-red-600">
                      <p>Có lỗi xảy ra khi tải dữ liệu lịch sử ngân hàng</p>
                      <Button
                        variant="outline"
                        onClick={() => window.location.reload()}
                        className="mt-4"
                      >
                        Thử lại
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ) : bankTransactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="px-4 py-12 text-center">
                    <p className="text-gray-500">
                      Không có dữ liệu lịch sử ngân hàng
                    </p>
                  </TableCell>
                </TableRow>
              ) : (
                bankTransactions.map((transaction, index) => (
                  <TableRow key={transaction.id || index}>
                    <TableCell>
                      {formatDateTime(transaction.createdAt)}
                    </TableCell>
                    <TableCell className="font-medium">
                      {getBankDisplayName(transaction.provider)}
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-green-100 text-green-800 border-green-200">
                        {formatCurrency(transaction.amount)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <p className="truncate max-w-[600px]">
                        {transaction.description}
                      </p>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>

        {/* Summary Bar */}
        {/* <div className="px-6 py-4 bg-green-50 border-t">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-500 rounded"></div>
              <span className="text-sm font-medium">
                Số tiền nạp: {formatCurrency(totalDeposit)}
              </span>
            </div>
          </div>
        </div> */}

        {/* Pagination */}
        <div className="flex items-center justify-between mt-6 px-6 pb-6">
          <div className="text-sm text-gray-500">
            Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
            {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
            {pagination.total} results
          </div>
          {pagination.totalPages > 1 && (
            <div className="flex items-center gap-2">
              <PaginationPrevious
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  if (pagination.hasPrev) handlePageChange(currentPage - 1)
                }}
                className={
                  !pagination.hasPrev ? 'pointer-events-none opacity-50' : ''
                }
              />
              <span className="text-sm font-medium px-2">
                {pagination.page} / {pagination.totalPages}
              </span>
              <PaginationNext
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  if (pagination.hasNext) handlePageChange(currentPage + 1)
                }}
                className={
                  !pagination.hasNext ? 'pointer-events-none opacity-50' : ''
                }
              />
            </div>
          )}
        </div>
      </Card>
    </div>
  )
}
